import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Spinner,
  Flex,
  Button,
  Checkbox,
  Radio,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Tooltip,
  ModalFooter,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { Header } from "build-ts/metadata";
import { getSortByLabel } from "utils/analysisUtils";
import showToast from "hooks/useCustomToast";
import { getListOfAnalysts, deleteAnalyst } from "services/AnalystService";
import CustomModal from "components/modals/CustomModal";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";

interface LibraryAnalystsProps {
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedItems?: Set<string>;
  hideActions?: boolean;
  singleSelection?: boolean;
  onClearSelection?: () => void;
}

const LibraryAnalysts: React.FC<LibraryAnalystsProps> = ({
  onSelectionChange,
  selectedItems: externalSelectedItems,
  hideActions = false,
  singleSelection = false,
  onClearSelection,
}) => {
  const { defaultTimeZone } = useUser();
  const [analysts, setAnalysts] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search filter with debounce
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
  });
  const filtersRef = useRef(filters);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasMountedRef = useRef(false);
  const fetchAnalystsRef = useRef<((searchQuery?: string) => Promise<void>) | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("CREATED");
  const [sortAscending, setSortAscending] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedAnalysts, setSelectedAnalysts] = useState<Set<string>>(externalSelectedItems || new Set());

  // Delete modal states
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const pageSize = 20;

  const fetchAnalysts = useCallback(
    async (searchQuery: string = filtersRef.current.search) => {
      setIsLoading(true);
      try {
        const response = await getListOfAnalysts(
          pageSize,
          currentPage,
          sortBy,
          sortAscending,
          searchQuery || undefined, // Pass undefined instead of empty string
          undefined // author - pass undefined instead of empty string
        );
        if (response) {
          setAnalysts(response?.headers || []);
          setTotalRows(response?.totalRows || 0);
        }
      } catch (error) {
        console.error("Error fetching analysts:", error);
        showToast("Error fetching analysts", "Failed to load analysts", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, currentPage, sortBy, sortAscending]
  );

  // Initialize and keep fetchAnalysts ref in sync
  fetchAnalystsRef.current = fetchAnalysts;
  useEffect(() => {
    fetchAnalystsRef.current = fetchAnalysts;
  }, [fetchAnalysts]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Debounced search effect - only triggers when search filter changes
  useEffect(() => {
    // Skip on initial mount - let the page/sort effect handle initial fetch
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to fetch after debounce delay
    debounceTimerRef.current = setTimeout(() => {
      // Use the latest function from ref to avoid stale closures
      if (fetchAnalystsRef.current) {
        fetchAnalystsRef.current(filtersRef.current.search);
      }
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // Only depend on filters.search to avoid double calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // Fetch analysts when page, sort, or sort order changes (immediate, no debounce)
  useEffect(() => {
    // Use the latest function from ref to avoid stale closures
    if (fetchAnalystsRef.current) {
      fetchAnalystsRef.current(filtersRef.current.search);
    }
    // Only depend on the actual values that should trigger a fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortAscending]);

  // Sync external selection changes
  useEffect(() => {
    if (externalSelectedItems) {
      setSelectedAnalysts(externalSelectedItems);
    }
  }, [externalSelectedItems]);

  const handleSearchChange = (value: string) => {
    setFilters({ search: value });
    // Also update the ref immediately
    filtersRef.current = { search: value };
  };

  const formatDate = (dateMs: number | undefined) => {
    if (!dateMs) return "N/A";
    try {
      const formatted = epochConverterWithTimeZone(dateMs || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone);
      return `${formatted} ${defaultTimeZone.toUpperCase()}`;
    } catch {
      return "N/A";
    }
  };

  // Handle individual analyst selection
  const handleAnalystSelection = (analystId: string) => {
    let newSelectedAnalysts: Set<string>;

    if (singleSelection) {
      // Single selection mode: select only one, or deselect if clicking the same one
      if (selectedAnalysts.has(analystId)) {
        newSelectedAnalysts = new Set<string>();
      } else {
        newSelectedAnalysts = new Set([analystId]);
      }
    } else {
      // Multi-selection mode: toggle selection
      newSelectedAnalysts = new Set(selectedAnalysts);
      if (newSelectedAnalysts.has(analystId)) {
        newSelectedAnalysts.delete(analystId);
      } else {
        newSelectedAnalysts.add(analystId);
      }
    }

    setSelectedAnalysts(newSelectedAnalysts);
    onSelectionChange?.(newSelectedAnalysts);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    const newSelectedAnalysts =
      selectedAnalysts.size === analysts.length ? new Set<string>() : new Set(analysts.map(analyst => analyst.id!));
    setSelectedAnalysts(newSelectedAnalysts);
    onSelectionChange?.(newSelectedAnalysts);
  };

  // Check if all analysts are selected
  const isAllSelected = analysts.length > 0 && selectedAnalysts.size === analysts.length;
  const isIndeterminate = selectedAnalysts.size > 0 && selectedAnalysts.size < analysts.length;

  const handleClearSelection = () => {
    setSelectedAnalysts(new Set());
    onSelectionChange?.(new Set());
    onClearSelection?.();
  };

  const handleDeleteAnalyst = async (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await deleteAnalyst(deleteId);

      // Close the confirmation modal
      setOpenDeleteModal(false);

      if (response && response.ok) {
        showToast("Analyst deleted successfully", "", "success");
        fetchAnalysts();
      } else {
        showToast("Error deleting analyst", "Failed to delete analyst", "error");
      }
      // Reset delete state
      setDeleteId(null);
    }
  };

  return (
    <VStack height="100%" align="stretch" spacing={0} overflow="hidden">
      {/* Search and Filter Section */}
      <HStack spacing={4} mb={2} justify="space-between" mx={4} flexShrink={0}>
        <HStack spacing={6}>
          <InputGroup size="md" maxW="400px">
            <InputLeftElement>
              <Icon as={AppIcons.search} color="gray.600" />
            </InputLeftElement>
            <Input
              placeholder="Search analysts..."
              value={filters.search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </InputGroup>

          {/* Sort By Dropdown */}
          <Flex align="center">
            <Text align="center" mb={0} mr={2} fontSize="sm" fontWeight="medium" color="fg.muted" whiteSpace="nowrap">
              Sort by:
            </Text>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<Icon as={AppIcons.downIcon} color="fg.muted" />}
                fontSize="sm"
                size="sm"
                rounded="md"
                color="fg.default"
                bg="bg.default"
                border="1px"
                borderColor="gray.300"
                _hover={{ bg: "purple.50" }}
                p={3}
                aria-label="Sort by"
                textAlign="left"
                width="200px"
                isTruncated
              >
                {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}
              </MenuButton>
              <MenuList>
                <MenuItem
                  key="last_modified_asc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Last Modified (Asc)
                </MenuItem>
                <MenuItem
                  key="last_modified_desc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Last Modified (Desc)
                </MenuItem>
                <MenuItem
                  key="created_asc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Created At (Asc)
                </MenuItem>
                <MenuItem
                  key="created_desc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Created At (Desc)
                </MenuItem>
                <MenuItem
                  key="name_asc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Name (Asc)
                </MenuItem>
                <MenuItem
                  key="name_desc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Name (Desc)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          {/* Clear selection button for single selection mode */}
          {singleSelection && selectedAnalysts.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Icon as={AppIcons.close} boxSize={4} />}
              colorScheme="gray"
              color="gray.600"
              _hover={{
                bg: "gray.100",
                color: "gray.800",
              }}
              fontWeight="500"
              onClick={handleClearSelection}
              flexShrink={0}
            >
              Clear Filters
            </Button>
          )}
          {selectedAnalysts.size > 0 && !hideActions && !singleSelection && (
            <Button
              colorScheme="purple"
              size="md"
              onClick={() => {
                // Open all selected analysts in new tabs
                selectedAnalysts.forEach(analystId => {
                  window.open(`/analyst/${analystId}`, "_blank");
                });
              }}
              isDisabled={selectedAnalysts.size === 0}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
              }}
            >
              Open {selectedAnalysts.size > 0 ? `(${selectedAnalysts.size})` : ""}
            </Button>
          )}
        </HStack>

        <HStack spacing={4}>
          {selectedAnalysts.size > 0 && !hideActions && !singleSelection && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium" m={0}>
              {selectedAnalysts.size} selected
            </Text>
          )}
          {singleSelection && selectedAnalysts.size > 0 && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium" m={0}>
              1 analyst selected
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted" mb={0}>
            {analysts.length} results
          </Text>
        </HStack>
      </HStack>

      {/* Table - scrollable area */}
      <TableContainer
        flex="1"
        minHeight={0}
        overflowY="auto"
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mr={4}
      >
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th
                  fontWeight="semibold"
                  fontSize="sm"
                  width="50px"
                  bg="purple.700"
                  color="white"
                  py={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}
                >
                  {!singleSelection && (
                    <Checkbox
                      isChecked={isAllSelected}
                      isIndeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      colorScheme="purple"
                    />
                  )}
                </Th>
                <Th
                  fontWeight="semibold"
                  fontSize="sm"
                  bg="purple.700"
                  color="white"
                  py={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}
                >
                  Analyst Name
                </Th>
                <Th
                  fontWeight="semibold"
                  fontSize="sm"
                  bg="purple.700"
                  color="white"
                  py={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}
                >
                  Author
                </Th>
                <Th
                  fontWeight="semibold"
                  fontSize="sm"
                  bg="purple.700"
                  color="white"
                  py={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}
                >
                  Created Date
                </Th>
                {!hideActions && (
                  <Th
                    fontWeight="semibold"
                    fontSize="sm"
                    bg="purple.700"
                    color="white"
                    py={1}
                    _hover={{ bg: "purple.600" }}
                    _active={{ bg: "purple.500" }}
                  >
                    Actions
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {analysts.length === 0 ? (
                <Tr>
                  <Td colSpan={hideActions ? 5 : 6} textAlign="center" py={8} color="fg.muted">
                    No analysts found
                  </Td>
                </Tr>
              ) : (
                analysts.map(analyst => (
                  <Tr
                    key={analyst.id}
                    _hover={{ bg: "gray.50" }}
                    bg={selectedAnalysts.has(analyst.id!) ? "purple.50" : "white"}
                  >
                    <Td>
                      {singleSelection ? (
                        <Radio
                          isChecked={selectedAnalysts.has(analyst.id!)}
                          onChange={() => handleAnalystSelection(analyst.id!)}
                          colorScheme="purple"
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <Checkbox
                          isChecked={selectedAnalysts.has(analyst.id!)}
                          onChange={() => handleAnalystSelection(analyst.id!)}
                          colorScheme="purple"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </Td>
                    <Td fontWeight="medium" color="fg.default" maxW="300px" whiteSpace="normal" wordBreak="break-word">
                      <Text mb={0}>{analyst.name || "Unnamed Analyst"}</Text>
                    </Td>
                    <Td color="fg.muted">{analyst.author_name || "Unknown"}</Td>
                    <Td color="fg.muted">{formatDate(analyst.created_date_ms)}</Td>
                    {!hideActions && (
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Open Analyst">
                            <IconButton
                              aria-label="Open Analyst"
                              icon={<Icon as={AppIcons.externalLink} />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "purple.500" }}
                              onClick={() => window.open(`/analyst/${analyst.id}`, "_blank")}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Analyst">
                            <IconButton
                              aria-label="Delete Analyst"
                              icon={<Icon as={AppIcons.delete} />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "red.500" }}
                              onClick={() => handleDeleteAnalyst(analyst.id!)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoading && totalRows > 0 && (
        <HStack justify="center" mt={6} spacing={2} flexShrink={0} pb={2}>
          {Array.from({ length: Math.ceil(totalRows / pageSize) }, (_, index) => index + 1).map(pageNumber => (
            <Button
              key={pageNumber}
              size="sm"
              variant={currentPage === pageNumber ? "solid" : "outline"}
              colorScheme={currentPage === pageNumber ? "purple" : "gray"}
              onClick={() => setCurrentPage(pageNumber)}
              minW="40px"
            >
              {pageNumber}
            </Button>
          ))}
        </HStack>
      )}

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Are you sure you want to delete this analyst?"
        size="sm"
        maxWidth="600px"
        maxHeight="200px"
        showCloseButton={false}
      >
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={handleDelete}>
            Delete
          </Button>
          <Button colorScheme="purple" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </CustomModal>
    </VStack>
  );
};

export default LibraryAnalysts;
